import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty, omit } from 'lodash';

import { DEFAULT_FILTERS_STATE, useFiltersState } from './useFiltersState';

import type { FiltersFlags, FiltersState } from '@/core/modules/MapPanelSources';

export interface FiltersContextData {
  filters: FiltersState;
  applyFilters(newFilters: Partial<FiltersState>): void;
  filterQuery: string;
  queryId: string;
  applyFilterQuery(query: FiltersContextData['filterQuery']): void;
  resetFilters(): void;
  hasFilters: boolean;
  updateDefaultDateRangeFilters: ReturnType<typeof useFiltersState>['updateDefaultDateRangeFilters'];
  preselectFilters: Partial<FiltersState>;
  setPreselectFilters: (filters: Partial<FiltersState>) => void;
  filterFlags?: FiltersFlags;
  setFilterFlags?: React.Dispatch<React.SetStateAction<FiltersFlags>>;
}

const FiltersContext = React.createContext<FiltersContextData>({
  filters: { ...DEFAULT_FILTERS_STATE },
  applyFilters: () => null,
  filterQuery: '',
  queryId: '',
  applyFilterQuery: () => null,
  resetFilters: () => null,
  updateDefaultDateRangeFilters: () => null,
  hasFilters: false,
  preselectFilters: {},
  setPreselectFilters: () => null,
});

export const useFiltersContext = () => React.useContext(FiltersContext);

const getQueryId = () => Date.now().toString();

export const FiltersContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [{ filterQuery, queryId }, setFilterQuery] = useState({ filterQuery: '', queryId: '' });
  const [preselectFilters, setPreselectFilters] = useState<Partial<FiltersState>>({});

  const { filters, defaultFilters, setFilters, updateDefaultDateRangeFilters, setFilterFlags, filterFlags } =
    useFiltersState();

  const applyFilters = useCallback((newFilters: Partial<FiltersState>) => {
    setFilters((currentFilters) => ({ ...currentFilters, ...newFilters }));
    setFilterQuery({ filterQuery: '', queryId: getQueryId() });

    // Clear preselectFilters for keys that are being explicitly set by user
    setPreselectFilters((prev) => {
      const keysToRemove = Object.keys(newFilters) as Array<keyof FiltersState>;
      const updated = { ...prev };
      keysToRemove.forEach((key) => {
        delete updated[key];
      });
      return updated;
    });
  }, []);

  const applyFilterQuery = useCallback((query: string) => {
    setFilterQuery({ filterQuery: query, queryId: getQueryId() });
  }, []);

  const resetFilters = useCallback(() => {
    applyFilters({ ...omit(defaultFilters, 'dateRange') }); // Reset all filters except date range
    setPreselectFilters({});
  }, [defaultFilters]);

  const mergedFilters = useMemo(
    () => ({ ...filters, ...preselectFilters }),
    [filters, preselectFilters]
  );

  const hasFilters = !!filterQuery || !isEmpty(Object.values(mergedFilters).flat());

  return (
    <FiltersContext.Provider
      value={{
        filters: mergedFilters,
        applyFilters,
        filterQuery,
        queryId,
        applyFilterQuery,
        resetFilters,
        updateDefaultDateRangeFilters,
        hasFilters,
        setFilterFlags,
        filterFlags,
        preselectFilters,
        setPreselectFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
